import React, { useMemo, useState } from 'react';
import { Smartphone, CircleCheck, Crown } from 'lucide-react';
import { useCompletedDays } from '../../hooks/useCompletedDays';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { StockCategoryFilter } from '../../types/stockCategories';
import { useEntitlement } from '../../hooks/useEntitlement';
import { UpgradePromptModal } from '../ui/modals/UpgradePromptModal';
import { cn } from '../../utils/cn';

interface PreviousDaysProps {
  category: StockCategoryFilter;
  className?: string;
}

/**
 * Compact horizontal strip showing the last 5 completed days for replay.
 * Premium users can click to replay; non-premium users see an upgrade prompt.
 */
export const PreviousDays: React.FC<PreviousDaysProps> = ({ category, className }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeDate = searchParams.get('date') || new Date().toISOString().split('T')[0];
  const { is_premium } = useEntitlement();
  const [showUpgrade, setShowUpgrade] = useState(false);

  const days = useMemo(() => {
    const result: { date: string; dayLabel: string; dateLabel: string; isToday: boolean }[] = [];
    const now = new Date();

    // Today first
    const todayIso = now.toISOString().split('T')[0];
    const todayDateLabel = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
    result.push({ date: todayIso, dayLabel: 'Today', dateLabel: todayDateLabel, isToday: true });

    for (let i = 1; i <= 5; i++) {
      const d = new Date(now);
      d.setUTCDate(d.getUTCDate() - i);
      const iso = d.toISOString().split('T')[0];
      const dayLabel = d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
      const dateLabel = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
      result.push({ date: iso, dayLabel, dateLabel, isToday: false });
    }
    return result;
  }, []);

  const dateStrings = useMemo(() => days.map((d) => d.date), [days]);
  const { completedDates } = useCompletedDays(category, dateStrings);

  const handleClick = (date: string, isToday: boolean) => {
    if (isToday) {
      navigate(`/${category}`);
    } else if (is_premium) {
      navigate(`/${category}?date=${date}`);
    } else {
      setShowUpgrade(true);
    }
  };

  return (
    <>
      <div className={cn('space-y-2 rounded-2xl bg-[#faf9f7] border border-black/[0.06] pt-[18px] px-5 pb-5', className)}>
        <span className="text-[13px] font-bold uppercase tracking-[0.06em] text-black/40">
          Play previous Wallstreetles
        </span>

        <div className="grid grid-cols-6 gap-2">
          {days.map(({ date, dayLabel, dateLabel, isToday }) => {
            const isSelected = date === activeDate;
            return (
              <button
                key={date}
                onClick={() => handleClick(date, isToday)}
                className={cn(
                  'relative flex flex-col items-center gap-1 rounded-xl bg-[#f0eeea] border border-black/[0.06] px-2 pt-3.5 pb-3 text-center transition-all duration-[250ms] ease-in-out',
                  'hover:bg-[#e8e6e1]',
                  isSelected && 'bg-[#1a1a1a] border-black/[0.15] hover:bg-[#1a1a1a]',
                  !isToday && !is_premium && 'opacity-70'
                )}
              >
                {!isToday && (
                  <Crown className={cn(
                    'absolute top-1 right-1 h-[11px] w-[11px] text-amber-600 opacity-60',
                    isSelected && 'opacity-100'
                  )} />
                )}
                {completedDates.has(date) ? (
                  <CircleCheck className={cn('h-4 w-4', isSelected ? 'text-white/60' : 'text-green-500')} />
                ) : (
                  <Smartphone className={cn('h-4 w-4', isSelected ? 'text-white/60' : 'text-black/20')} />
                )}
                <span className={cn('text-[13px] font-bold', isSelected ? 'text-white' : 'text-black/75')}>{dayLabel}</span>
                <span className={cn('text-[11px] font-medium leading-tight', isSelected ? 'text-white/60' : 'text-black/35')}>{dateLabel}</span>
              </button>
            );
          })}
        </div>

        <button
          onClick={() => navigate(`/${category}/archive`)}
          className="w-full rounded-xl bg-[#e8e6e1] border border-black/[0.06] py-2.5 text-[11px] font-bold uppercase tracking-[0.08em] text-black/40 hover:bg-[#dfddd8] transition-colors duration-200"
        >
          View Past 30 Days &rarr;
        </button>
      </div>

      <UpgradePromptModal
        isOpen={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        title="Unlock Past Games"
        message="Upgrade to a Wallstreetle Membership to unlock 250+ archived games across all 9 categories."
        requiresPremium={true}
        feature="Past Games"
      />
    </>
  );
};
